import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Check, Pencil, Loader2, Lock, ChevronDown } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import { useParticipant } from '../../../hooks/useParticipant';
import type { AaiPolicy, WhatsAllowedData, ToolDelegation } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, RiskPill, PageHeader, Banner } from '../dashboards/ui';

// TD-4: What's Allowed reads the SAME action catalog + org rules the dive walks —
// no more static text. Expand a tool to see its capabilities, with anything IT
// switched off labeled as policy rather than quietly missing.
const RISK_TONE: Record<string, string> = {
  read: 'bg-gray-100 text-gray-600',
  draft: 'bg-brand-50 text-brand-700',
  act: 'bg-amber-50 text-amber-700',
  destructive: 'bg-red-50 text-red-600',
};

const CompanyToolCapabilities: React.FC = () => {
  const [intake, setIntake] = useState<{ tools: string[]; noDestructive: boolean } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Record<string, ToolDelegation & { rules?: { no_destructive: boolean } }>>({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await aaiApi.getOrgToolStack();
        if (data.tool_stack.length) setIntake({ tools: data.tool_stack, noDestructive: data.no_destructive });
      } catch { /* IT hasn't supplied a stack yet — the card just doesn't render */ }
    })();
  }, []);

  const toggle = async (tool: string) => {
    if (open === tool) { setOpen(null); return; }
    setOpen(tool);
    if (!catalog[tool]) {
      try { const { data } = await aaiApi.getToolActions(tool); setCatalog((c) => ({ ...c, [tool]: data })); }
      catch { /* leave collapsed content empty */ }
    }
  };

  if (!intake) return null;
  return (
    <Card>
      <MonoLabel className="mb-1">Your company's tools — what AI may do in each</MonoLabel>
      <p className="text-[12px] text-gray-500 mb-3">
        The tools IT cleared, with each one's real capability list.
        {intake.noDestructive && ' Destructive actions are off company-wide — policy, not preference.'}
      </p>
      <div className="space-y-1.5">
        {intake.tools.map((tool) => {
          const cat = catalog[tool];
          const isOpen = open === tool;
          return (
            <div key={tool} className="bg-white rounded-lg border border-gray-100">
              <button onClick={() => toggle(tool)} className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left">
                <span className="text-[13px] font-semibold text-gray-800 flex-1 min-w-0 truncate">{tool}</span>
                {cat && <span className="text-[11px] text-gray-400 shrink-0">{cat.total_available} actions</span>}
                <ChevronDown size={14} className={`text-gray-300 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && cat && (
                <div className="px-3.5 pb-3 pt-0.5 space-y-1 border-t border-gray-50">
                  {cat.actions.map((a) => (
                    <div key={a.key} className="flex items-center gap-2 text-[12px]">
                      {a.blocked ? <Lock size={11} className="text-gray-300 shrink-0" /> : <Check size={12} className="text-emerald-500 shrink-0" />}
                      <span className={`flex-1 min-w-0 truncate ${a.blocked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{a.label}</span>
                      {a.blocked
                        ? <span className="text-[10.5px] text-gray-400 shrink-0">company policy</span>
                        : <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0 ${RISK_TONE[a.risk] || ''}`}>{a.risk}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// Company org AI policy — read by everyone (also shown in Module 1), edited by
// champions/trainers/owners (manage_program).
const OrgPolicyCard: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const [policy, setPolicy] = useState<AaiPolicy | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await aaiApi.getPolicy(); setPolicy(data); } catch { /* optional */ }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await aaiApi.setPolicy(draft); setPolicy(data); setEditing(false); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-2 mb-3">
        <MonoLabel>Organization AI policy</MonoLabel>
        {canManage && !editing && (
          <button onClick={() => { setDraft(policy?.body || ''); setEditing(true); }}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-500 hover:text-brand-600">
            <Pencil size={13} /> {policy?.body?.trim() ? 'Edit' : 'Add policy'}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={8}
            placeholder="Write your organization's AI policy — acceptable use, data handling, approved tools…"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
          <div className="flex items-center gap-2 mt-2">
            <button disabled={saving} onClick={save}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-semibold px-4 py-2 whitespace-nowrap">
              {saving && <Loader2 size={13} className="animate-spin" />} Save policy
            </button>
            <button onClick={() => setEditing(false)} className="text-[12.5px] text-gray-500 px-2">Cancel</button>
          </div>
        </div>
      ) : policy?.body?.trim() ? (
        <p className="text-[13.5px] text-gray-700 whitespace-pre-wrap leading-relaxed">{policy.body}</p>
      ) : (
        <p className="text-[13px] text-gray-400">
          {canManage ? 'No policy published yet — add one so your team sees it in Module 1.' : 'Your organization hasn’t published its AI policy yet.'}
        </p>
      )}
    </Card>
  );
};

const WhatsAllowedPage: React.FC = () => {
  const { context } = useParticipant();
  const dept = context?.department;
  const { data, loading, error } = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), 'Could not load this.');
  if (loading) return <Loading label="Loading…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="What's allowed"
        subtitle="What your company has said yes to, and what it has not. If something you want to do is not here, ask."
      />

      <Banner tone="green" icon={<ShieldCheck size={16} className="text-emerald-600 shrink-0" />}>
        These are the approved use cases and ground rules{dept ? <> for <b>{dept}</b></> : ''} — kept current by your AI champion.
      </Banner>

      <div className="space-y-4">
        <OrgPolicyCard canManage={!!context?.capabilities?.manage_program} />

        {/* TD-4: the live capability view — same catalog + rules as the dives. */}
        <CompanyToolCapabilities />

        <Card>
          <MonoLabel className="mb-3">Approved use cases{dept ? ` in ${dept}` : ''}</MonoLabel>
          {data.approved_use_cases.length === 0 ? (
            <p className="text-[13px] text-gray-400">None approved yet — request one and it routes to your champion.</p>
          ) : (
            <div className="space-y-2">
              {data.approved_use_cases.map((u, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white rounded-lg px-3.5 py-2.5 border border-gray-100">
                  <Check size={15} className="text-emerald-500 shrink-0" />
                  <span className="text-[13px] font-medium flex-1 min-w-0 truncate">{u.name}</span>
                  <span className="text-[11.5px] text-gray-400 whitespace-nowrap">{u.department}</span>
                  <RiskPill risk={u.risk} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {(data.approved_tools || []).length > 0 && (
          <Card>
            <MonoLabel className="mb-3">Approved tools · evaluated &amp; announced</MonoLabel>
            <div className="space-y-2.5">
              {(data.approved_tools || []).map((t) => (
                <div key={t.id} className="bg-white rounded-lg px-3.5 py-3 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <ShieldCheck size={15} className="text-emerald-500 shrink-0" />
                    <span className="text-[13px] font-semibold">{t.title}</span>
                    {t.tools.map((name) => (
                      <span key={name} className="rounded-full bg-gray-100 text-gray-600 px-2.5 py-0.5 text-[11px] font-medium">{name}</span>
                    ))}
                  </div>
                  {t.recommendation && <p className="text-[12.5px] text-gray-500 m-0 leading-relaxed">{t.recommendation}</p>}
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <MonoLabel className="mb-3">Tools in use across your company</MonoLabel>
          {data.tools_in_use.length === 0 ? (
            <p className="text-[13px] text-gray-400">No AI tools reported yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.tools_in_use.map((t) => (
                <span key={t.value} className="rounded-full bg-white border border-gray-200 px-3 py-1 text-[12.5px] text-gray-700">
                  {t.label} <span className="text-gray-400">· {t.users}</span>
                </span>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <MonoLabel className="mb-3">Ground rules</MonoLabel>
          <ul className="space-y-2">
            {data.guidelines.map((g, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-gray-700">
                <Check size={15} className="text-brand-500 mt-0.5 shrink-0" />{g}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default WhatsAllowedPage;
